import getLocal from '@/app/utils/getLocal';
import { ENGLISH_LANG, rpcMap } from '@/constants/lang';
import { Button, Col, Form, FormInstance, Input, Row, Select } from 'antd';
import { message } from 'antd-v5';
import { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { pkQuoteCreate } from '../../api';
import css from './index.module.scss';

const formList = [
    {
        cols: [
            {
                name: 'firstName',
                type: 'input',
                required: true,
                placeholder: 'First Name*',
            },
            {
                name: 'lastName',
                type: 'input',
                required: false,
                placeholder: 'Last Name (Optional)',
            },
        ],
    },
    {
        cols: [
            {
                name: 'address',
                type: 'input',
                required: false,
                placeholder: 'Address (Optional)',
            },
            {
                name: 'states',
                type: 'input',
                required: true,
                placeholder: 'State*',
            },
        ],
    },
    {
        cols: [
            {
                name: 'zipCode',
                type: 'input',
                required: false,
                placeholder: 'ZIP Code (Optional)',
            },
            {
                name: 'email',
                type: 'input',
                required: true,
                placeholder: 'Email*',
            },
        ],
    },
    {
        cols: [
            {
                name: 'phoneNumber',
                type: 'input',
                required: true,
                placeholder: 'Phone Number*',
            },
            {
                name: 'vehicleModel',
                type: 'select',
                required: true,
                placeholder: 'Vehicle Model*',
                options: [
                    { value: 'Class A' },
                    { value: 'Class B' },
                    { value: 'Class C' },
                    { value: 'Travel Trailer' },
                    { value: 'Fifth Wheel Trailer' },
                    { value: 'SUV&Pick-up' },
                    { value: 'Truck Camper' },
                    { value: 'Super C' },
                    { value: 'Others' },
                ],
            },
        ],
    },
    {
        cols: [
            {
                name: 'vehicleChassisModel',
                type: 'input',
                required: true,
                placeholder: 'Vehicle Chassis Model*',
            },
            {
                name: 'batteryPowerRequirement',
                type: 'select',
                required: true,
                placeholder: 'Battery Power Requirement*',
                options: [{ value: 'Under 10kWh' }, { value: '10-15kWh' }, { value: '15-30kWh' }, { value: 'Over 30kWh' }],
            },
        ],
    },
];

interface ConsultingDialogProps {
    show: boolean;
    onSubmit?: (val: any) => void;
    onClose?: () => void;
}

export default function ConsultingDialog({ show, onSubmit, onClose }: ConsultingDialogProps) {
    const { locale } = getLocal();
    const [form] = Form.useForm();
    const [recaptchaResponse, setRecaptchaResponse] = useState('');
    const recaptchaRef = useRef(null);
    const hl = rpcMap[ENGLISH_LANG.includes(locale) ? 'en' : locale];
    const SECRET_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_REGISTER_SECRET_KEY;

    const formRef = useRef<FormInstance>();

    const handleClose = () => {
        onClose?.();
        if (!formRef.current) return;
        formRef.current.resetFields?.();
    };

    const onReCAPTCHAChange = async (captchaCode: string) => {
        if (!captchaCode) {
            return;
        }
        setRecaptchaResponse(captchaCode);
    };

    const finish = (params: any) => {
        pkQuoteCreate({
            recaptchaResponse,
            source: 'Power Kits 5kVA Product',
            ...params,
        });
        handleClose();
        message.success('Submit successfully');
    };
    if (!show) return null;
    return ReactDOM.createPortal(
        <div className={`head-consulting-dialog ${css['TheComp']}`}>
            <div className="head-consulting-dialog-container">
                <svg
                    className="head-consulting-dialog-close"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    onClick={handleClose}
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="10" cy="10" r="10" fill="white" fillOpacity="0.4" />
                    <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M13.1872 7.53033C13.4801 7.23744 13.4801 6.76256 13.1872 6.46967C12.8943 6.17678 12.4194 6.17678 12.1265 6.46967L9.82873 8.76746L7.53093 6.46967C7.23804 6.17678 6.76317 6.17678 6.47027 6.46967C6.17738 6.76256 6.17738 7.23744 6.47027 7.53033L8.76807 9.82812L6.46967 12.1265C6.17678 12.4194 6.17678 12.8943 6.46967 13.1872C6.76256 13.4801 7.23744 13.4801 7.53033 13.1872L9.82873 10.8888L12.1271 13.1872C12.42 13.4801 12.8949 13.4801 13.1878 13.1872C13.4807 12.8943 13.4807 12.4194 13.1878 12.1265L10.8894 9.82812L13.1872 7.53033Z"
                        fill="white"
                    />
                </svg>
                <div className="head-consulting-dialog-title">We're Here to Help</div>
                <div className="head-consulting-dialog-subtitle">Fill out the form below, and our sales engineer team will contact you.</div>
                <Form className="head-consulting-dialog-form" form={form} onFinish={finish} ref={formRef}>
                    {formList.map((i, idx) => (
                        <Row gutter={24} key={idx}>
                            {i.cols.map(({ type, placeholder, required, name, options }, idx2) => (
                                <Col span={24} md={12} key={`${idx}-${idx2}`}>
                                    <Form.Item name={name} rules={[{ required }]}>
                                        {type === 'input' ? (
                                            <Input placeholder={placeholder} maxLength={150} />
                                        ) : (
                                            <Select
                                                placeholder={placeholder}
                                                options={options}
                                                getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                                            />
                                        )}
                                    </Form.Item>
                                </Col>
                            ))}
                        </Row>
                    ))}
                    {/* <Form.Item name="answer" rules={[{ required: true }]}>
                        <Select
                            placeholder="Do you already own a vehicle power system*"
                            options={[
                                { value: 1, label: 'Yes' },
                                { value: 0, label: 'No' },
                            ]}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                        />
                    </Form.Item> */}
                    <Form.Item name="answer2" rules={[{ required: true }]}>
                        <Select
                            placeholder="Do you have an existing RV power system?*"
                            options={[
                                { value: 1, label: 'Yes' },
                                { value: 0, label: 'No' },
                            ]}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                        />
                    </Form.Item>
                    <Form.Item name="answer3" rules={[{ required: true }]} className="item-rv-choose2">
                        <Select
                            placeholder="Would you prefer to install it yourself, work with an electrician or RV dealer you know, or choose one of our certified electricians?**"
                            options={[
                                { value: 'Install by myself', label: 'Install by myself' },
                                { value: 'work with my electrician', label: 'work with my electrician' },
                                { value: 'choose a certified electrician', label: 'choose a certified electrician' },
                            ]}
                            getPopupContainer={(triggerNode: any) => triggerNode.parentNode || document.body}
                        />
                    </Form.Item>
                    <div className="head-consulting-dialog-tip-a1">
                        **If you prefer to handle the installation yourself, we will provide free remote assistance, but there may be a waiting list for this
                        service.
                    </div>
                    <Form.Item name="recaptchaResponse" rules={[{ required: false }]}>
                        {/* @ts-ignore */}
                        <ReCAPTCHA ref={recaptchaRef} hl={hl} sitekey={SECRET_KEY} onChange={onReCAPTCHAChange} />
                    </Form.Item>
                    <Form.Item>
                        <Button className="head-consulting-dialog-submit" type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>,
        document.body,
    );
}
